import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ILeadView } from '../../viewModels/leads/interfaces';
import { queryKeys } from './queryKeys';
import { fromILeadView } from './types';

export type ToggleDecisionMakerVariables = {
	companyId: string;
	contactIds: string[];
};

/**
 * Toggle the decision-maker set for a lead. PUT body matches what
 * `LeadViewModel.toggleDecisionMaker` sends (`{ contactIds }`); the backend
 * returns the full updated `ILeadView`. We invalidate the lead + contacts
 * queries so the UI reflects the new DM state immediately.
 */
export function useToggleDecisionMaker(): UseMutationResult<ILeadView, Error, ToggleDecisionMakerVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<ILeadView, Error, ToggleDecisionMakerVariables>({
		mutationFn: async ({ companyId, contactIds }) => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${companyId}/DecisionMakers` }),
				'PUT',
				{ contactIds }
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value;
		},
		onSuccess: (data, variables) => {
			// The PUT response carries the up-to-date decisionMakersContactIds,
			// so prime the lead query cache directly to skip a refetch round-trip.
			if (data) {
				queryClient.setQueryData(queryKeys.focusedView.lead(variables.companyId), fromILeadView(data));
			}
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(variables.companyId) });
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.contacts(variables.companyId) });
		},
	});
}
